<script setup lang="ts">
import type {
  KLineMarketWorkbenchMarketConfig,
  KLineMarketWorkbenchProps,
  KLineMarketWorkbenchSlots,
  KLineWorkbenchOptionContext,
  KLineWorkbenchOptionFactory,
} from '@wiki/kline-vue'
import { defineAsyncComponent, type AllowedComponentProps, type ComponentCustomProps, type VNodeProps } from 'vue'
import {
  type BrokerSymbol,
  type CategoryTranslationMap,
  type KLinePoint,
  type Period,
  type SymbolCategory,
  createPageAccessToken,
  fetchCategoryTranslations,
  fetchInitConfig,
  fetchSymbolCategories,
  getErrorMessage,
} from '~/api/wikitrade-market'
import {
  DEFAULT_PERIOD,
  DEFAULT_SYMBOL,
  MAIN_SERIES_ID,
  PERIOD_DEFAULT_BAR_SPACING,
  PERIOD_OPTIONS,
  WS_CONFIG,
  type PageStatus,
  type SocketStatus,
} from './market/constants'
import { createWikiTradeDatafeedAdapter, encryptSocketToken, type WikiTradeDatafeedAdapter } from './market/datafeed'
import { formatPrice, formatSymbolName, getPrecision, mergeRealtimePoint } from './market/utils'

const envApi = useEnvApi()
const { locale, localeMeta, t } = useAppI18n()
const wikiTradeBaseURL = envApi.wikitrade

useSeoMeta({
  title: t('84126'),
  ogTitle: t('84126'),
  description: t('84105'),
  ogDescription: t('84105'),
})

const chartLocale = computed(() => {
  return localeMeta.language === 'ar-SA' ? 'ar' : localeMeta.language
})

const KLineMarketWorkbench = defineAsyncComponent(async () => {
  const klineVue = await import('@wiki/kline-vue')

  return klineVue.KLineMarketWorkbench
}) as unknown as new () => {
  $props: KLineMarketWorkbenchProps & VNodeProps & AllowedComponentProps & ComponentCustomProps
  $slots: KLineMarketWorkbenchSlots
  $emit: {
    (event: 'ready', chart: unknown): void
  }
}

type WorkbenchDatafeedAdapter = NonNullable<KLineMarketWorkbenchMarketConfig['datafeedAdapter']>

// 品种选择弹窗的所有远程数据集中放在 picker，避免和主图行情状态互相污染。
interface PickerState {
  symbols: BrokerSymbol[]
  categories: SymbolCategory[]
  categoryTranslations: CategoryTranslationMap
  errorMessage: string | null
  loaded: boolean
}

// accessToken 是所有 WikiTrade 行情接口的入口条件；拿到 token 后再初始化 datafeed 和品种列表。
const accessToken = ref('')
const route = useRoute()
const code = route.query.code as string | undefined
const symbol = ref(code || DEFAULT_SYMBOL)
const symbolDialogOpen = ref(false)
const adapter = shallowRef<(WorkbenchDatafeedAdapter & Pick<WikiTradeDatafeedAdapter, 'destroy'>) | null>(null)

// picker 负责弹窗内搜索、分类和错误提示；loaded 只表示弹窗基础数据是否已经完成首轮加载。
const picker = reactive<PickerState>({
  symbols: [],
  categories: [],
  categoryTranslations: {},
  errorMessage: null,
  loaded: false,
})

// market 负责 K 线工作台主区域：历史数据、页面状态、实时连接状态和错误提示。
const market = reactive<{
  bars: KLinePoint[]
  status: PageStatus
  socketStatus: SocketStatus
  errorMessage: string | null
}>({
  bars: [],
  status: 'idle',
  socketStatus: 'idle',
  errorMessage: null,
})

const activeSymbol = computed(() => picker.symbols.find((item) => item.symbol === symbol.value))
const displayName = computed(() =>
  activeSymbol.value ? formatSymbolName(activeSymbol.value) : symbol.value || 'WikiTrade',
)

// 精度优先取品种配置，接口缺失时用最后一根 K 线价格兜底推断。
const precision = computed(() => getPrecision(activeSymbol.value, market.bars.at(-1)?.close))
const pickerLoading = computed(() => Boolean(accessToken.value && !picker.loaded))
// K 线 option 每次由工作台上下文生成，价格精度、主序列 ID 和当前展示名称都来自页面状态。
const chartOption = (_context: KLineWorkbenchOptionContext): ReturnType<KLineWorkbenchOptionFactory> => ({
  theme: {
    extends: 'light',
    components: {
      axis: {
        line: {
          color: 'rgba(15, 23, 42, 0.35)',
        },
      },
      grid: {
        paneSeparator: {
          color: 'rgba(15, 23, 42, 0.15)',
        },
      },
      paneResize: {
        activeLineColor: '#2563eb',
        activeShadow: 'none',
        activeBackground: 'rgba(37, 99, 235, 0.06)',
        hoverBackground: 'rgba(15, 23, 42, 0.04)',
        hoverShadow: 'none',
      },
    },
  },
  meta: {
    name: displayName.value,
  },
  yAxis: {
    label: {
      precision: precision.value,
      format: (price: number) => formatPrice(price, precision.value),
    },
  },
  priceLine: {
    label: {
      precision: precision.value,
    },
  },
})

const openSymbolDialog = () => {
  symbolDialogOpen.value = true
}

const selectSymbol = (nextSymbol: string) => {
  symbol.value = nextSymbol
  market.bars = []
  market.errorMessage = null
  market.status = 'loading'
  symbolDialogOpen.value = false
}

onMounted(async () => {
  try {
    market.status = 'loading'
    accessToken.value = await createPageAccessToken(wikiTradeBaseURL, {
      languageCode: locale,
    })
  } catch (error) {
    market.status = 'error'
    market.errorMessage = getErrorMessage(error)
  }
})

// token 可用后创建 datafeed。datafeed 的生命周期跟 token 绑定，避免旧 token 下的 WS 继续推送。
watch(
  accessToken,
  async (token, _previousToken, onCleanup) => {
    if (!token) return

    let cancelled = false
    let nextAdapter: (WorkbenchDatafeedAdapter & Pick<WikiTradeDatafeedAdapter, 'destroy'>) | null = null
    onCleanup(() => {
      cancelled = true
      nextAdapter?.destroy()
      if (adapter.value === nextAdapter) adapter.value = null
    })

    try {
      const wsToken = await encryptSocketToken(WS_CONFIG.pid, WS_CONFIG.accountId)
      if (cancelled) return

      nextAdapter = createWikiTradeDatafeedAdapter({
        baseURL: wikiTradeBaseURL,
        languageCode: locale,
        getAccessToken: () => accessToken.value,
        getWsToken: () => wsToken,
        onRealtimeUpdate: (bar) => {
          market.bars = mergeRealtimePoint(market.bars, bar)
        },
        onSocketStatusChange: (status) => {
          market.socketStatus = status
        },
        onLoadingStatusChange: (loading) => {
          market.status = loading && market.bars.length === 0 ? 'loading' : 'ready'
        },
        onDataReplaced: (bars) => {
          market.bars = bars
        },
        onError: (error) => {
          market.errorMessage = getErrorMessage(error)
          market.status = 'error'
        },
      }) as WorkbenchDatafeedAdapter & Pick<WikiTradeDatafeedAdapter, 'destroy'>
      adapter.value = nextAdapter
    } catch (error) {
      if (!cancelled) {
        market.status = 'error'
        market.errorMessage = getErrorMessage(error)
      }
    }
  },
  { immediate: true },
)

// token 可用后加载弹窗基础数据。三个接口互不依赖，所以并行请求并分别容错。
watch(
  accessToken,
  async (token, _previousToken, onCleanup) => {
    if (!token) return

    let cancelled = false
    onCleanup(() => {
      cancelled = true
    })

    picker.loaded = false
    picker.errorMessage = null

    const [symbolsResult, categoriesResult, translationsResult] = await Promise.allSettled([
      fetchInitConfig(wikiTradeBaseURL, token, { languageCode: locale }),
      fetchSymbolCategories(wikiTradeBaseURL, token, { languageCode: locale }),
      fetchCategoryTranslations(wikiTradeBaseURL, token, { languageCode: locale }),
    ])
    if (cancelled) return

    const nextSymbols = symbolsResult.status === 'fulfilled' ? symbolsResult.value : []
    picker.symbols = nextSymbols
    picker.categories = categoriesResult.status === 'fulfilled' ? categoriesResult.value : []
    picker.categoryTranslations = translationsResult.status === 'fulfilled' ? translationsResult.value : {}
    picker.errorMessage =
      [symbolsResult, categoriesResult, translationsResult]
        .map((result) => (result.status === 'rejected' ? getErrorMessage(result.reason) : null))
        .filter(Boolean)
        .join('；') || null
    picker.loaded = true

    const hasValidSymbol = nextSymbols.some((item) => item.symbol === symbol.value)
    if (!hasValidSymbol) {
      symbol.value = nextSymbols[0]?.symbol || ''
    }
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  adapter.value?.destroy()
})
</script>

<template>
  <main class="wikitrade-market-page">
    <!-- 主图工作台：ClientOnly 避免 K 线组件在 SSR 阶段访问浏览器 API。 -->
    <ClientOnly>
      <KLineMarketWorkbench
        :market="{
          symbol,
          bars: market.bars,
          datafeedAdapter: adapter,
          datafeedKey: 'wikitrade',
          mainSeriesId: MAIN_SERIES_ID,
        }"
        :workspace="{
          storageKey: 'wiki-kline:vue:wikitrade:workspaces:v1',
          enableAutosave: true,
        }"
        :toolbar="{
          defaultPeriod: DEFAULT_PERIOD,
          periodOptions: PERIOD_OPTIONS,
        }"
        :display="{
          name: displayName,
          precision,
          priceFormatter: formatPrice,
          status: market.status,
          errorMessage: market.errorMessage,
        }"
        :sidebar="{
          panels: ['objects'],
        }"
        :locale="chartLocale"
        :chart="{
          getDefaultBarSpacing: (nextResolution: string) => PERIOD_DEFAULT_BAR_SPACING[nextResolution as Period] ?? 20,
          option: chartOption,
        }"
      >
        <!-- 自定义品种选择入口：保持工作台 toolbar 风格，同时接入本页弹窗筛选逻辑。 -->
        <template #symbolSelector>
          <MarketSymbolSelectorTrigger
            :display-name="displayName"
            :loading="pickerLoading"
            @open="openSymbolDialog"
          />
        </template>
      </KLineMarketWorkbench>
    </ClientOnly>

    <!-- 品种选择弹窗：搜索、分类和接口错误提示都收敛在 picker 状态里。 -->
    <MarketSymbolPickerDialog
      v-model="symbolDialogOpen"
      :symbols="picker.symbols"
      :categories="picker.categories"
      :category-translations="picker.categoryTranslations"
      :current-symbol="symbol"
      :loading="pickerLoading"
      :error-message="picker.errorMessage"
      @select="selectSymbol"
    />
  </main>
</template>

<style scoped lang="less">
// 页面使用视口高度撑满 K 线工作台，顶部 70px 对齐站点固定导航。
.wikitrade-market-page {
  position: relative;
  display: flex;
  height: calc(100vh - 70px);
  background: #f5f7fb;
  margin-top: 70px;
  border-top: 1px solid #e5e7eb;
}

// K 线组件异步加载期间的轻量占位，避免首屏出现空白区域。
.wikitrade-market-fallback {
  display: grid;
  width: 100%;
  height: 100%;
  place-items: center;
  color: #64748b;
  font-size: 14px;
}
</style>
